import { Box, Card, Container, Grid, Typography } from "@material-ui/core";
import {
  getBuilderData,
  getBuilderPermissions,
  getConfig,
  getUserData,
  getPorfoliCard,
  postSectionOrder,
} from "adapters";
import axios, { AxiosResponse } from "axios";
import { BusinessCard } from "components/business-card";
import { AppStoreList } from "components/shared/app-store-list";
import { FileCarousel } from "components/shared/file-carousel";
import { LcardBadge } from "components/shared/lcard-badge";
import { PageLoader } from "components/shared/loaders/page-loader";
import { ModuleSettings } from "components/shared/module-settings";
import { PaymentOptionList } from "components/shared/payment-options-list";
import { PermissionsControl } from "components/shared/permission-control";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { SetConfigData } from "store/config/actions";
import {
  GetUrlBuilderData,
  selectBuilderPermissions,
  SetBuilderPermissions,
  SetUrlBuilderData,
  SetUrlBuilderPublish,
} from "store/url-builder";
import { AboutMe } from "./about-me";
import { VideoUpload } from "./add-video";
import { AppStores } from "./app-stores";
import { ContactMe } from "./contact-me";
import { DesignerControlBar } from "./control-bar";
import { CustomCTA } from "./custom-CTA";
import { Information } from "./information";
import { SocialNetworks } from "./social-network/socials-networks";
import { Testimonials } from "./testimonials";
import { findPermissionByModule } from "utils/permissions";
import { UserData } from "types/userData";
import { types } from "node-sass";
import { GetCardData, SetCard } from "store/card";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { SectionValues } from "utils/sections";
import { LoaderWithOverlay } from "components/shared/loader-with-overlay";
import { BuilderPermissions } from "types/permissions";

export interface UrlDesignerProps {
  builderType: string;
}

export default function URLDesigner({ builderType }: UrlDesignerProps) {
  const [loading, setLoading] = useState(true);
  const [isOrderUpdating, setIsOrderUpdating] = useState(false);
  const [userData, setUserData] = useState<UserData>({
    userPlan: "FREE",
    userId: -1,
  });
  const dispatch = useDispatch();
  const { cardId }: { cardId: string } = useParams();
  const builderData = useSelector(GetUrlBuilderData);
  const portCard = useSelector(GetCardData);
  const permissions = useSelector(selectBuilderPermissions);
  const [permissionSetId, setPermissionSetId] = useState(0);

  const [sections, setSections] = useState(SectionValues);
  const [prevSections, setPrevSections] = useState(SectionValues);

  const [isDragging, setIsDragging] = useState(false);
  const [draggingId, setDraggingId] = useState<any>(null);

  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount((prev) => prev + 1);
  }, [draggingId]);

  useEffect(() => {
    axios
      .all(
        [
          getBuilderData(cardId, builderType),
          getConfig(),
          getBuilderPermissions(cardId, builderType, "false"),
        ].concat(
          builderType === "PRO"
            ? [getPorfoliCard(cardId), getUserData(cardId)]
            : [
                Promise.resolve({ data: {} } as AxiosResponse),
                Promise.resolve({
                  data: {
                    success: true,
                    message: "UserData fetched",
                    data: {
                      userPlan: "FREE",
                      userId: -1,
                    },
                  },
                } as AxiosResponse),
              ]
        )
      )
      .then(
        axios.spread(
          (
            { data: builderData },
            { data: configData },
            { data: permissionsData },
            { data: card },
            { data: userData }
          ) => {
            dispatch(SetConfigData(configData));
            builderType === "PRO" && dispatch(SetCard(card));
            dispatch(
              SetBuilderPermissions(permissionsData.data.objectPermissions)
            );
            setPermissionSetId(permissionsData.data.objectPermissionSetId);
            dispatch(SetUrlBuilderData(builderData.data));
            setUserData(userData.data);
            setLoading(false);
          }
        )
      );
  }, [dispatch, cardId, builderType]);

  //update section order
  const updateSectionOrder = (items: typeof SectionValues) => {
    setIsOrderUpdating(true);
    postSectionOrder(cardId, builderType, items)
      .then(({ data }) => {
        dispatch(SetUrlBuilderPublish(false));
      })
      .catch((err: any) => {
        setSections(prevSections);
      })
      .finally(() => {
        setIsOrderUpdating(false);
      });
  };

  //reorder
  const reorder = (
    list: typeof SectionValues,
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list);
    const removed = result.splice(startIndex, 1);
    result.splice(endIndex, 0, ...removed);
    return result;
  };

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    setDraggingId(null);

    // dropped outside the list
    if (!result.destination) return;
    const items = reorder(
      sections,
      result.source.index,
      result.destination.index
    );
    const isSame = sections.every((item, index) => item === items[index]);
    setPrevSections(sections);
    setSections(items);
    if (!isSame) updateSectionOrder(items);
  };

  const onDragStart = (result: DropResult) => {
    setDraggingId(result.draggableId);
    setIsDragging(true);
  };

  useEffect(() => {
    const order = builderData.order || [];
    const sortedSections = sections
      .slice(0)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b));

    setSections(sortedSections);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderData.order]);

  useEffect(() => {
    let lastPoint = { x: 0, y: 0 };
    let isScrolled = false;

    const scrollListner = () => {
      isScrolled = true;
    };

    const mousemoveListner = (e: MouseEvent) => {
      window.addEventListener("scroll", scrollListner);
      if (draggingId) {
        const x = document.getElementById(draggingId);
        if (x?.clientHeight && x?.clientHeight >= 400) {
          if (!isScrolled) {
            if (e.clientY > lastPoint.y) {
              window.scrollBy({
                left: 0,
                top: 200,
                behavior: "smooth",
              });
            } else if (e.clientY < lastPoint.y) {
              window.scrollBy({
                left: 0,
                top: -200,
                behavior: "smooth",
              });
            }
          }
        }
      }
      lastPoint.x = e.clientX;
      lastPoint.y = e.clientY;
    };

    window.addEventListener("mousemove", mousemoveListner);
    return () => {
      window.removeEventListener("mousemove", mousemoveListner);
      window.removeEventListener("scroll", scrollListner);
    };
  }, [draggingId, isDragging]);

  if (loading) return <PageLoader />;

  return (
    <>
      {isOrderUpdating && <LoaderWithOverlay />}
      <Container maxWidth="sm" className="root-wrapper">
        <Grid container justifyContent="center">
          <Grid item container xs={12} spacing={0}>
            {/* <Grid item xs={12}>
       <LcardBadge />
      </Grid> */}
            <Grid item xs={12}>
              <BusinessCard
                cardId={cardId}
                builderType={builderType}
                cardImage={portCard.cardImage}
              />
            </Grid>
            <Grid item xs={12} className="mt-4">
              <Card style={{ overflow: "visible" }}>
                <Grid container style={{ padding: "2em" }} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Information
                      <ModuleSettings
                        permissions={findPermissionByModule(2, permissions)}
                        builderType={builderData.urlDesignType}
                      />
                    </Typography>
                    <PermissionsControl
                      permissions={findPermissionByModule(2, permissions)}
                      builderType={builderData.urlDesignType}
                    >
                      <Information builderType={builderType} />
                    </PermissionsControl>
                  </Grid>

                  <DragDropContext
                    onDragEnd={onDragEnd}
                    onDragStart={onDragStart}
                  >
                    <Droppable droppableId="droppable">
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="dnd-wrapper"
                          id="dnd-wrapper"
                        >
                          {sections.map((section, index) => (
                            <Draggable
                              key={section}
                              draggableId={section}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.dragHandleProps}
                                  {...provided.draggableProps}
                                  key={index}
                                >
                                  {" "}
                                  {section === "about" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="about"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        About
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            9,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          9,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <AboutMe builderType={builderType} />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "social" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="social"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Social Networks
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            10,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          10,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <SocialNetworks
                                          builderType={builderType}
                                        />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "video" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="video"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Video
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            11,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          11,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <VideoUpload
                                          builderType={builderType}
                                        />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "gallery" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="gallery"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Gallery
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            14,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          14,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                        readonlyForChangablePro
                                      >
                                        <FileCarousel
                                          files={builderData.images}
                                          themeColor={
                                            builderData.primaryColor.color
                                          }
                                          enablePreview
                                          builderType={builderType}
                                          slidesPerRow={2}
                                        />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "testimonials" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="testimonials"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Testimonials
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            17,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          17,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                        readonlyForChangablePro
                                      >
                                        <Testimonials
                                          builderType={builderType}
                                        />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "cta" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="cta"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Custom CTA Button
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            23,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          23,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <CustomCTA builderType={builderType} />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "apps" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="apps"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        App. Stores
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            24,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          24,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <AppStores builderType={builderType} />
                                        <Box marginTop="1em">
                                          <AppStoreList
                                            appStores={builderData.appStores}
                                            onItemClick={(store) =>
                                              window.open(store.storeLink)
                                            }
                                          />
                                        </Box>
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "payment" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="payment"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Payment Options
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            25,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          25,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <PaymentOptionList
                                          paymentOptions={
                                            builderData.paymentOptions
                                          }
                                          builderType={builderType}
                                        />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                  {section === "reachout" && (
                                    <Grid
                                      item
                                      xs={12}
                                      className="section-wrapper"
                                      id="reachout"
                                    >
                                      <Typography variant="h6" gutterBottom>
                                        Reach Out
                                        <ModuleSettings
                                          permissions={findPermissionByModule(
                                            26,
                                            permissions
                                          )}
                                          builderType={
                                            builderData.urlDesignType
                                          }
                                        />
                                      </Typography>
                                      <PermissionsControl
                                        permissions={findPermissionByModule(
                                          26,
                                          permissions
                                        )}
                                        builderType={builderData.urlDesignType}
                                      >
                                        <ContactMe builderType={builderType} />
                                      </PermissionsControl>
                                    </Grid>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {!isDragging && (
        <DesignerControlBar
          builderType={builderType}
          userData={userData}
          permissions={findPermissionByModule(
            BuilderPermissions.colourPicker,
            permissions
          )}
          permissionSetId={permissionSetId}
        />
      )}
    </>
  );
}
